/**
 * ListDeletionCoordinator - Orchestrates optimistic list deletion so the UI
 * updates instantly and the network round-trip happens in the background.
 *
 * Two flows:
 *   - deleteWithMove: deletedListId disappears, its tasks are remapped to
 *     targetListId in the cache (and pulled into useTodoState on hydrate).
 *   - deleteAll: deletedListId disappears, its tasks are wiped from the cache.
 *
 * On API failure both flows fully revert (cache + lists state) and surface a
 * toast. Snapshots are captured up-front so revert never depends on observing
 * the live state at failure time.
 */

import { TaskListsApiClient } from './TaskListsApiClient';
import { TodoCache } from './TodoCache';
import type { Task } from '@/types/tasks';
import type { TaskList } from '@/types/task-lists';

type ListsUpdater = (updater: (prev: TaskList[]) => TaskList[]) => void;

type CoordinatorDeps = {
  userId: string;
  client: TaskListsApiClient;
  setLists: ListsUpdater;
  onPendingTargetChange: (targetListId: string | null) => void;
  onError: (message: string) => void;
};

type DeleteWithMoveArgs = {
  deletedListId: string;
  targetListId: string;
  movedTasks: Task[];
};

type DeleteAllArgs = {
  deletedListId: string;
};

type Snapshots = {
  lists: TaskList[];
  tasksByListId: Map<string, Task[] | null>;
};

export class ListDeletionCoordinator {
  private readonly deps: CoordinatorDeps;
  private inFlight = new Set<string>();

  constructor(deps: CoordinatorDeps) {
    this.deps = deps;
  }

  isInFlight(deletedListId: string): boolean {
    return this.inFlight.has(deletedListId);
  }

  async deleteWithMove(args: DeleteWithMoveArgs): Promise<void> {
    const { deletedListId, targetListId, movedTasks } = args;
    if (this.inFlight.has(deletedListId)) return;

    const snapshots = await this.captureSnapshots([deletedListId, targetListId]);
    this.applyOptimisticMove(deletedListId, targetListId, movedTasks, snapshots);
    this.inFlight.add(deletedListId);
    this.deps.onPendingTargetChange(targetListId);

    try {
      await this.deps.client.deleteList(deletedListId, {
        action: 'move',
        targetListId,
      });
    } catch (error) {
      this.revert(snapshots);
      this.deps.onError(this.errorMessage(error, 'Couldn’t move tasks. Try again.'));
    } finally {
      this.inFlight.delete(deletedListId);
      this.deps.onPendingTargetChange(null);
    }
  }

  async deleteAll(args: DeleteAllArgs): Promise<void> {
    const { deletedListId } = args;
    if (this.inFlight.has(deletedListId)) return;

    const snapshots = await this.captureSnapshots([deletedListId]);
    this.applyOptimisticDelete(deletedListId, snapshots);
    this.inFlight.add(deletedListId);

    try {
      await this.deps.client.deleteList(deletedListId, { action: 'delete' });
    } catch (error) {
      this.revert(snapshots);
      this.deps.onError(this.errorMessage(error, 'Couldn’t delete list. Try again.'));
    } finally {
      this.inFlight.delete(deletedListId);
    }
  }

  private async captureSnapshots(listIds: string[]): Promise<Snapshots> {
    const tasksByListId = new Map<string, Task[] | null>();
    await Promise.all(
      listIds.map(async (id) => {
        const cached = await TodoCache.getTasks(this.deps.userId, id);
        tasksByListId.set(id, cached);
      }),
    );
    let listsSnapshot: TaskList[] = [];
    this.deps.setLists((prev) => {
      listsSnapshot = prev;
      return prev;
    });
    return { lists: listsSnapshot, tasksByListId };
  }

  private applyOptimisticMove(
    deletedListId: string,
    targetListId: string,
    movedTasks: Task[],
    snapshots: Snapshots,
  ): void {
    const targetCached = snapshots.tasksByListId.get(targetListId) ?? [];
    const baseOrderIndex = targetCached.reduce(
      (max, t) => Math.max(max, t.orderIndex),
      -1,
    );
    const remapped: Task[] = movedTasks.map((task, idx) => ({
      ...task,
      listId: targetListId,
      orderIndex: baseOrderIndex + 1 + idx,
    }));
    const merged = [...targetCached, ...remapped];

    void TodoCache.setTasks(this.deps.userId, targetListId, merged);
    void TodoCache.setTasks(this.deps.userId, deletedListId, []);
    this.deps.setLists((prev) => {
      const next = prev.filter((l) => l.id !== deletedListId);
      void TodoCache.setLists(this.deps.userId, next);
      return next;
    });
  }

  private applyOptimisticDelete(
    deletedListId: string,
    _snapshots: Snapshots,
  ): void {
    void TodoCache.setTasks(this.deps.userId, deletedListId, []);
    this.deps.setLists((prev) => {
      const next = prev.filter((l) => l.id !== deletedListId);
      void TodoCache.setLists(this.deps.userId, next);
      return next;
    });
  }

  private revert(snapshots: Snapshots): void {
    this.deps.setLists(() => {
      void TodoCache.setLists(this.deps.userId, snapshots.lists);
      return snapshots.lists;
    });
    snapshots.tasksByListId.forEach((tasks, listId) => {
      if (tasks === null) {
        void TodoCache.setTasks(this.deps.userId, listId, []);
      } else {
        void TodoCache.setTasks(this.deps.userId, listId, tasks);
      }
    });
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }
}
